import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, AuthError } from '../services/auth';
import { useSession } from '../contexts';

const WALLET_INSTALL_URL =
  'https://chrome.google.com/webstore/detail/concordium-wallet/mnnkpffndmickbiakofclnpoiajlegmg';

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login: sessionLogin } = useSession();
  const navigate = useNavigate();

  const handleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await login();
      sessionLogin(result.accountAddress);
      navigate('/');
    } catch (e) {
      if (e instanceof AuthError) {
        if (e.code === 'USER_REJECTED') {
          setError('You rejected the signature request.');
        } else if (e.code === 'WALLET_NOT_FOUND') {
          setError(e.message);
        } else {
          setError(e.message);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img src="/alba.svg" alt="TPP Alba" className="mx-auto h-16 w-16" />
          <h1 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
            TPP Alba
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Payment Services Portal
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-6">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-900">
              Sign in with your wallet
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Connect your Concordium Browser Wallet to authenticate
            </p>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin text-white"
                  style={{ width: '20px', height: '20px' }}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    style={{ opacity: 0.25 }}
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    style={{ opacity: 0.75 }}
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Connecting...
              </>
            ) : (
              'Login with Concordium Wallet'
            )}
          </button>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              Don&apos;t have the wallet?{' '}
              <a
                href={WALLET_INSTALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Install Concordium Browser Wallet
              </a>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400">
          Secured by Concordium blockchain identity verification
        </p>
      </div>
    </div>
  );
}
